import { clsx } from '@alexis/helpers/clsx';
import CheckboxItem from '@pages/FlightSearchResult/components/CheckboxItem';
import { DEFAULT_NUMBER_OF_OPTIONS_SHOWED } from '@pages/FlightSearchResult/constants';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import Switch from '@components/Switch';

import { convertFilterStateToDataForTracking } from '@helpers/flight';
import { convertFlightSearchResultFarePriceToPrice } from '@helpers/price';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { getIsRtlState, getPriceOptionsState, getTranslationsState } from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

type StopoverAirportsFilterProps = {
  disableCollapse?: boolean;
  className?: string;
  genzoTrackApplyFilterActionEvent: any;
  genzoTrackApplyFilterSearchView: () => void;
};

const StopoverAirportsFilter: React.FC<StopoverAirportsFilterProps> = ({
  disableCollapse,
  className,
  genzoTrackApplyFilterActionEvent,
  genzoTrackApplyFilterSearchView,
}) => {
  const { passengerCount, isMultiCity } = useFlightSearchPageParams();
  const { isPerLegSearch } = useFlightPLS();

  const {
    stopoverOptionsFilter,
    stopoverAirportCodesFilter,
    activeLeg,
    departLegFilterOption,
    toggleMultiCitySingleFilter,
    selectAllFilterOptions,
    clearFilter,
  } = useFlightSearchFilters();

  const { exchangeRate, stopoverAirportFilterOptions } = useFlightSearchResults();

  const activeLegStopoverAirportFilterOptions = isMultiCity
    ? departLegFilterOption?.leg?.stopoverAirports.map(
        (airport, index) =>
          ({
            id: index,
            name: airport.code,
            price: airport.price,
            city: airport.city,
            airport: airport.airport,
          } as FlightMetaSearchFilterOption),
      ) || []
    : stopoverAirportFilterOptions;

  const [isStopoverAirportsOptionsExpanded, setIsStopoverAirportsOptionsExpanded] = useState(false);

  const translations = useSelector(getTranslationsState);
  const isRtl = useSelector(getIsRtlState);
  const priceOptions = useSelector(getPriceOptionsState);

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const handleStopoverOptionChange = (option: string) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      value: option,
      searchParamKey: FlightSearchResultSearchParams.StopoverOptions,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      no_change_airports_stopover: convertFilterStateToDataForTracking(
        newFilter,
        false,
        false,
        isPerLegSearch,
        activeLeg,
      ),
    });
    genzoTrackApplyFilterSearchView();
  };

  const handleStopoverAirportClick = (code: string) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      value: code,
      searchParamKey: FlightSearchResultSearchParams.Stopovers,
    });

    genzoTrackApplyFilterSearchView();

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      transit_airports: convertFilterStateToDataForTracking(
        newFilter,
        false,
        false,
        isPerLegSearch,
        activeLeg,
      ),
    });
  };

  const handleSelectAllStopoverAirportsClick = () => {
    selectAllFilterOptions({
      searchParamKey: FlightSearchResultSearchParams.Stopovers,
      filterOptions: activeLegStopoverAirportFilterOptions,
      activeLeg,
    });

    genzoTrackApplyFilterSearchView();
  };

  const handleClearAllStopoverAirportsClick = () => {
    clearFilter({
      searchParamKey: FlightSearchResultSearchParams.Stopovers,
      activeLeg,
    });

    genzoTrackApplyFilterSearchView();
  };

  const selectedStopoverAirportForActiveLeg =
    stopoverAirportCodesFilter.find((item) => item.leg === activeLeg)?.values || [];

  const groupedStopoverAirportsByCity = (() => {
    const unsortedGroupedStopoverAirportsByCity = activeLegStopoverAirportFilterOptions.reduce(
      (
        accumulator: {
          city: FlightSearchResultCity;
          stopoverAirports: FlightMetaSearchFilterOption[];
        }[],
        stopoverAirport: FlightMetaSearchFilterOption,
      ) => {
        const groupedStopoverAirportByCity = accumulator.find(
          (groupedStopoverAirportByCity) =>
            groupedStopoverAirportByCity.city.name === (stopoverAirport.city?.name || ''),
        );

        if (groupedStopoverAirportByCity) {
          groupedStopoverAirportByCity.stopoverAirports.push(stopoverAirport);
        } else {
          accumulator.push({
            city: stopoverAirport.city || { code: '', countryCode: '', name: '' },
            stopoverAirports: [stopoverAirport],
          });
        }
        return accumulator;
      },
      [] as {
        city: FlightSearchResultCity;
        stopoverAirports: FlightMetaSearchFilterOption[];
      }[],
    );

    const sortedGroupedStopoverAirportsByCity = unsortedGroupedStopoverAirportsByCity.sort((a, b) =>
      a.city.name.localeCompare(b.city.name),
    );

    return sortedGroupedStopoverAirportsByCity.flatMap((item) =>
      item.stopoverAirports.map((stopoverAirport, index) => ({
        stopoverAirport,
        city: index === 0 ? item.city : undefined,
      })),
    );
  })();

  return (
    <div className={clsx(styles.categoryContent, className && styles.inDropdown)}>
      {!isMultiCity ? (
        <div
          className={clsx(styles.itemBoolean)}
          onClick={() => handleStopoverOptionChange('same_airport')}
        >
          {translations.no_airports}

          <Switch
            isRtl={isRtl}
            checked={(
              stopoverOptionsFilter.find((item) => item.leg === activeLeg)?.values || []
            ).includes('same_airport')}
          />
        </div>
      ) : null}

      {(disableCollapse || isStopoverAirportsOptionsExpanded
        ? groupedStopoverAirportsByCity
        : groupedStopoverAirportsByCity.slice(0, DEFAULT_NUMBER_OF_OPTIONS_SHOWED)
      ).map((groupedStopoverAirportItem) => {
        return (
          <React.Fragment key={groupedStopoverAirportItem.stopoverAirport.id}>
            {groupedStopoverAirportItem.city?.name ? (
              <div className={styles.itemGroupHeader}>{groupedStopoverAirportItem.city.name}</div>
            ) : null}

            <CheckboxItem
              className={className}
              key={groupedStopoverAirportItem.stopoverAirport.name}
              isChecked={selectedStopoverAirportForActiveLeg.includes(
                groupedStopoverAirportItem.stopoverAirport.name || '',
              )}
              label={groupedStopoverAirportItem.stopoverAirport.airport?.name ?? ''}
              exchangeRate={exchangeRate}
              price={convertFlightSearchResultFarePriceToPrice(
                groupedStopoverAirportItem.stopoverAirport.price!,
                passengerCount,
                priceOptions.showTotalPrice,
                true,
              )}
              onCheckboxToggle={() =>
                handleStopoverAirportClick(groupedStopoverAirportItem.stopoverAirport.name)
              }
            />
          </React.Fragment>
        );
      })}

      {!disableCollapse &&
      groupedStopoverAirportsByCity.length > DEFAULT_NUMBER_OF_OPTIONS_SHOWED ? (
        <div
          className={clsx(styles.showMore, isStopoverAirportsOptionsExpanded && styles.expanded)}
          onClick={() => setIsStopoverAirportsOptionsExpanded((prev) => !prev)}
        >
          {isStopoverAirportsOptionsExpanded ? translations.show_less : translations.show_more}
          <i className={clsx(styles.chevron, styles.bottom)}></i>
        </div>
      ) : null}

      {!disableCollapse && groupedStopoverAirportsByCity.length > 1 ? (
        <div className={styles.selectAllContainer}>
          <span onClick={handleSelectAllStopoverAirportsClick}>{translations.select_all}</span>
          <i className={styles.divider}></i>
          <span onClick={handleClearAllStopoverAirportsClick}>{translations.clear}</span>
        </div>
      ) : null}
    </div>
  );
};

export default StopoverAirportsFilter;
